'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { MutableRefObject } from 'react'
import { loadYoutubeIframeApi, type YTPlayer } from '@/lib/load-youtube-iframe-api'
import type { ShortsAudioControl } from '@/lib/shorts-audio-control'
import { registerShortsAudioControl } from '@/lib/shorts-audio-session'
import styles from '@/components/shorts/shorts.module.css'

const YT_PLAYING = 1
/** Only hard-rewind a warm peer on promote if it drifted this far (seconds). */
const PROMOTE_REWIND_MIN_S = 2.25

interface ShortYoutubePlayerProps {
  youtubeId: string
  isActive: boolean
  shouldPreload?: boolean
  shouldWarm?: boolean
  /** Single feed host: keep one iframe and swap videos with loadVideoById. */
  shared?: boolean
  playing: boolean
  muted: boolean
  volume?: number
  audioUnlocked?: boolean
  /** Stagger neighbour warming so +2 does not cold-start on first swipes. */
  warmDelayMs?: number
  /**
   * While the warm host is entering the viewport, do not rewind the playhead —
   * seekTo mid-swipe is the visible "sekme" users report.
   */
  suppressWarmRewind?: boolean
  audioControlRef?: MutableRefObject<ShortsAudioControl | null>
  onTimeUpdate?: (current: number, duration: number) => void
  onPlayingChange?: (playing: boolean) => void
}

interface PlaybackSurfacePointer {
  pointerId: number
  startX: number
  startY: number
  moved: boolean
}

type YoutubeShortsHandle = {
  currentTime: number
  duration: number
  muted: boolean
  playing: boolean
  volume: number
  pause: () => void
  destroy: () => void
  mute: () => void
  playVideo: () => void
  pauseVideo: () => void
  seekTo: (seconds: number) => void
  loadVideoById: (videoId: string) => void
}

function clampVolume(value: number): number {
  return Math.min(100, Math.max(0, Number.isFinite(value) ? value : 100))
}

function wrapYoutubePlayer(yt: YTPlayer): YoutubeShortsHandle {
  const safe = <T,>(fn: () => T, fallback: T): T => {
    try {
      return fn()
    } catch {
      return fallback
    }
  }

  return {
    get currentTime() {
      return safe(() => Number(yt.getCurrentTime()) || 0, 0)
    },
    get duration() {
      return safe(() => Number(yt.getDuration()) || 0, 0)
    },
    get muted() {
      return safe(() => yt.isMuted(), true)
    },
    set muted(next) {
      safe(() => {
        if (next) yt.mute()
        else yt.unMute()
      }, undefined)
    },
    get playing() {
      return safe(() => yt.getPlayerState() === YT_PLAYING, false)
    },
    get volume() {
      return safe(() => (Number(yt.getVolume?.()) || 0) / 100, 0)
    },
    set volume(next) {
      safe(() => {
        yt.setVolume(Math.round(Math.min(1, Math.max(0, next)) * 100))
      }, undefined)
    },
    pause() {
      safe(() => yt.pauseVideo(), undefined)
    },
    destroy() {
      safe(() => yt.destroy(), undefined)
    },
    mute() {
      safe(() => yt.mute(), undefined)
    },
    playVideo() {
      safe(() => yt.playVideo(), undefined)
    },
    pauseVideo() {
      safe(() => yt.pauseVideo(), undefined)
    },
    seekTo(seconds) {
      safe(() => yt.seekTo(Math.max(0, seconds), true), undefined)
    },
    loadVideoById(videoId) {
      safe(() => yt.loadVideoById(videoId), undefined)
    },
  }
}

function buildYoutubeEmbedUrl(startsWithAutoplay: boolean, videoId: string) {
  const params = new URLSearchParams({
    autoplay: startsWithAutoplay ? '1' : '0',
    controls: '0',
    playsinline: '1',
    mute: '1',
    rel: '0',
    iv_load_policy: '3',
    modestbranding: '1',
    disablekb: '1',
    fs: '0',
    loop: '1',
    playlist: videoId,
    enablejsapi: '1',
  })
  if (typeof window !== 'undefined' && window.location.origin) {
    params.set('origin', window.location.origin)
    params.set('widget_referrer', window.location.href)
  }
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`
}

export default function ShortYoutubePlayer({
  youtubeId,
  isActive,
  shouldPreload = false,
  shouldWarm = false,
  shared = false,
  playing,
  muted,
  volume = 50,
  audioUnlocked = false,
  warmDelayMs = 0,
  suppressWarmRewind = false,
  audioControlRef,
  onTimeUpdate,
  onPlayingChange,
}: ShortYoutubePlayerProps) {
  const mountRef = useRef<HTMLDivElement>(null)
  const hostRef = useRef<HTMLDivElement>(null)
  const youtubeProgressRef = useRef<HTMLInputElement>(null)
  const playerRef = useRef<YoutubeShortsHandle | null>(null)
  const loadedVideoIdRef = useRef<string | null>(null)
  const youtubeIdRef = useRef(youtubeId)
  const isActiveRef = useRef(isActive)
  const playingRef = useRef(playing)
  const mutedRef = useRef(muted)
  const volumeRef = useRef(clampVolume(volume))
  const audioUnlockedRef = useRef(audioUnlocked)
  const hasStartedPlaybackRef = useRef(false)
  const hasPlaybackProgressRef = useRef(false)
  const hasActivePlaybackProgressRef = useRef(false)
  const playerApiReadyRef = useRef(false)
  const playWatchdogRef = useRef<number | undefined>(undefined)
  const progressTimerRef = useRef<number | undefined>(undefined)
  const shouldWarmRef = useRef(shouldWarm)
  const shouldPreloadRef = useRef(shouldPreload)
  const warmBufferedRef = useRef(false)
  const warmInFlightRef = useRef(false)
  const warmTimerRef = useRef<number | undefined>(undefined)
  const warmDelayMsRef = useRef(warmDelayMs)
  /** One seek-to-0 per activate; repeated sync must not rewind a starting short. */
  const activateKickRef = useRef(false)
  /**
   * After promote, block feed progress until the playhead is back near 0.
   * Warm peers keep decoding ahead; reporting that time makes the shared bar jump.
   */
  const promoteRewindPendingRef = useRef(false)
  const promoteRewindStartedAtRef = useRef(0)
  const lastWarmRewindAtRef = useRef(0)
  const suppressWarmRewindRef = useRef(suppressWarmRewind)
  const seekingRef = useRef(false)
  const playbackSurfacePointerRef = useRef<PlaybackSurfacePointer | null>(null)
  const suppressPlaybackSurfaceClickUntilRef = useRef(0)
  const callbacksRef = useRef({
    onTimeUpdate,
    onPlayingChange,
  })
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  /** Hide YouTube's native poster until a real frame is buffered. */
  const [frameReady, setFrameReady] = useState(false)
  const shouldKeepPlayer = shared || isActive || shouldPreload || shouldWarm

  useEffect(() => {
    youtubeIdRef.current = youtubeId
  }, [youtubeId])

  useEffect(() => {
    warmDelayMsRef.current = warmDelayMs
  }, [warmDelayMs])

  useEffect(() => {
    suppressWarmRewindRef.current = suppressWarmRewind
  }, [suppressWarmRewind])

  useEffect(() => {
    const becameActive = isActive && !isActiveRef.current
    if (becameActive) {
      const player = playerRef.current
      const currentTime = player ? Number(player.currentTime) || 0 : 0
      // Promote a warm peer seamlessly. Do NOT seek near the start — seekTo(0)
      // while the short is already on screen is the visible swipe "sekme".
      const alreadyBuffered =
        warmBufferedRef.current || currentTime > 0.12 || player?.playing === true
      if (alreadyBuffered) {
        hasActivePlaybackProgressRef.current = true
        hasStartedPlaybackRef.current = true
        hasPlaybackProgressRef.current = true
        activateKickRef.current = true
        setFrameReady(true)
        if (player && currentTime >= PROMOTE_REWIND_MIN_S) {
          promoteRewindPendingRef.current = true
          promoteRewindStartedAtRef.current = Date.now()
          try {
            player.seekTo(0)
          } catch {
            /* ignore */
          }
        } else {
          promoteRewindPendingRef.current = false
        }
      } else {
        hasActivePlaybackProgressRef.current = false
        hasStartedPlaybackRef.current = false
        hasPlaybackProgressRef.current = false
        activateKickRef.current = false
        promoteRewindPendingRef.current = false
      }
      suppressPlaybackSurfaceClickUntilRef.current = Date.now() + 700
      warmInFlightRef.current = false
    } else if (!isActive) {
      hasActivePlaybackProgressRef.current = false
      activateKickRef.current = false
      promoteRewindPendingRef.current = false
    }
    isActiveRef.current = isActive
    playingRef.current = playing
    mutedRef.current = muted
    volumeRef.current = clampVolume(volume)
    audioUnlockedRef.current = audioUnlocked
    shouldWarmRef.current = shouldWarm
    shouldPreloadRef.current = shouldPreload
  }, [
    audioUnlocked,
    isActive,
    muted,
    playing,
    shouldPreload,
    shouldWarm,
    volume,
  ])

  useEffect(() => {
    if (isActive) {
      suppressPlaybackSurfaceClickUntilRef.current = Date.now() + 700
    }
  }, [isActive, youtubeId])

  useEffect(() => {
    callbacksRef.current = {
      onTimeUpdate,
      onPlayingChange,
    }
  }, [onPlayingChange, onTimeUpdate])

  const canUnmute = useCallback(() => {
    // Only an explicit unlock from the mute/volume controls may unmute.
    // hasPlaybackActivation / playback progress alone cause "UI unmuted,
    // stream silent" after autoplay policy strips the audio.
    return audioUnlockedRef.current
  }, [])

  const applyAudio = useCallback(
    (player: YoutubeShortsHandle) => {
      const nextVolume = volumeRef.current / 100
      const unlocked = canUnmute()
      const userWantsMute =
        mutedRef.current || nextVolume <= 0 || !unlocked
      // Inactive pool peers must stay hard-muted. Volume 0 alone still leaks
      // a faint YouTube stream after the feed has been unlocked.
      if (!isActiveRef.current) {
        player.volume = 0
        player.muted = true
        return
      }
      const outputVolume = userWantsMute ? 0 : nextVolume
      player.volume = outputVolume
      if (userWantsMute) {
        // Keep muted for silence; unlock path unmutes only the active host.
        player.muted = true
      } else {
        player.muted = false
      }
    },
    [canUnmute],
  )

  useEffect(() => {
    const control: ShortsAudioControl = {
      applyUserAudio: (nextMuted, nextVolume) => {
        audioUnlockedRef.current = true
        mutedRef.current = nextMuted
        volumeRef.current = clampVolume(nextVolume)
        const player = playerRef.current
        if (!player) return
        applyAudio(player)
        if (!nextMuted && isActiveRef.current && playingRef.current) {
          try {
            player.playVideo()
          } catch {
            /* ignore */
          }
        }
      },
      seekTo: (seconds) => {
        const player = playerRef.current
        if (!player || !isActiveRef.current) return
        const duration = Math.max(0, Number(player.duration) || 0)
        const nextTime = Math.min(
          duration || Number.MAX_SAFE_INTEGER,
          Math.max(0, Number(seconds) || 0),
        )
        try {
          player.seekTo(nextTime)
          if (playingRef.current) player.playVideo()
        } catch {
          /* ignore */
        }
        hasActivePlaybackProgressRef.current = nextTime > 0.05
        hasStartedPlaybackRef.current = true
        activateKickRef.current = true
      },
      getPlaybackTime: () => {
        const player = playerRef.current
        if (!player) return { current: 0, duration: 0 }
        return {
          current: Math.max(0, Number(player.currentTime) || 0),
          duration: Math.max(0, Number(player.duration) || 0),
        }
      },
    }
    if (audioControlRef) audioControlRef.current = control
    const unregister = registerShortsAudioControl(control)
    return () => {
      unregister()
      if (audioControlRef?.current === control) audioControlRef.current = null
    }
  }, [applyAudio, audioControlRef])

  const pausePlayer = useCallback((player: YoutubeShortsHandle) => {
    try {
      player.pauseVideo()
    } catch {
      /* iframe may already be disposed */
    }
  }, [])

  const clearWarmTimer = useCallback(() => {
    if (warmTimerRef.current) {
      window.clearTimeout(warmTimerRef.current)
      warmTimerRef.current = undefined
    }
  }, [])

  /** Quietly pull a real first buffer for neighbours (no spinner / no audio bleed). */
  const warmBuffer = useCallback(
    (player: YoutubeShortsHandle) => {
      if (
        warmBufferedRef.current ||
        warmInFlightRef.current ||
        isActiveRef.current ||
        !playerApiReadyRef.current ||
        playerRef.current !== player ||
        !shouldWarmRef.current
      ) {
        return
      }

      warmInFlightRef.current = true
      clearWarmTimer()

      const startWarm = () => {
        if (
          isActiveRef.current ||
          playerRef.current !== player ||
          !playerApiReadyRef.current
        ) {
          warmInFlightRef.current = false
          return
        }
        // Warm only while hard-muted so neighbours never bleed audio.
        player.volume = 0
        player.muted = true
        try {
          player.playVideo()
        } catch {
          /* provider may not be attached yet */
        }

        let attempts = 0
        const settle = () => {
          warmTimerRef.current = undefined
          if (playerRef.current !== player || !playerApiReadyRef.current) {
            warmInFlightRef.current = false
            return
          }
          // Became active mid-warm: keep playing, do not pause.
          if (isActiveRef.current) {
            warmBufferedRef.current = true
            warmInFlightRef.current = false
            return
          }
          const currentTime = Number(player.currentTime) || 0
          if (currentTime > 0.12) {
            warmBufferedRef.current = true
            warmInFlightRef.current = false
            // Keep playing hard-muted, but hold the playhead near the start so
            // promote does not open with a half-consumed progress bar.
            player.volume = 0
            player.muted = true
            if (currentTime > 0.85 && !suppressWarmRewindRef.current) {
              try {
                player.seekTo(0.05)
              } catch {
                /* ignore */
              }
            }
            setFrameReady(true)
            return
          }
          if (attempts >= 20) {
            // Failed to buffer — allow a later warmBuffer retry on sync.
            warmInFlightRef.current = false
            player.volume = 0
            player.muted = true
            return
          }
          attempts += 1
          try {
            player.playVideo()
          } catch {
            /* retry */
          }
          warmTimerRef.current = window.setTimeout(settle, 280)
        }
        warmTimerRef.current = window.setTimeout(settle, 320)
      }

      const delay = Math.max(0, Number(warmDelayMsRef.current) || 0)
      if (delay > 0) {
        warmTimerRef.current = window.setTimeout(startWarm, delay)
      } else {
        startWarm()
      }
    },
    [clearWarmTimer, pausePlayer],
  )

  const updateYoutubeProgress = useCallback((player: YoutubeShortsHandle) => {
    const input = youtubeProgressRef.current
    if (!input) return

    const duration = Math.max(0, Number(player.duration) || 0)
    const currentTime = Math.min(
      duration || Number.MAX_SAFE_INTEGER,
      Math.max(0, Number(player.currentTime) || 0),
    )
    input.max = String(duration || 100)
    if (!seekingRef.current) input.value = String(currentTime)
    const displayedValue = Number(input.value) || 0
    const progress =
      duration > 0 ? Math.min(100, Math.max(0, (displayedValue / duration) * 100)) : 0
    input.style.setProperty('--shorts-progress', `${progress}%`)
  }, [])

  const clearPlayWatchdog = useCallback(() => {
    if (playWatchdogRef.current) {
      window.clearTimeout(playWatchdogRef.current)
      playWatchdogRef.current = undefined
    }
  }, [])

  const armPlayWatchdog = useCallback(
    (player: YoutubeShortsHandle) => {
      clearPlayWatchdog()
      let retryCount = 0
      let lastObservedTime = Number(player.currentTime) || 0
      let advancingSamples = 0

      const verifyPlayback = () => {
        playWatchdogRef.current = undefined
        if (playerRef.current !== player || !isActiveRef.current || !playingRef.current) {
          return
        }

        const currentTime = Number(player.currentTime) || 0
        if (currentTime > lastObservedTime + 0.08) {
          advancingSamples += 1
          lastObservedTime = currentTime
          if (advancingSamples >= 2) {
            hasActivePlaybackProgressRef.current = true
            hasStartedPlaybackRef.current = true
            hasPlaybackProgressRef.current = true
            applyAudio(player)
            return
          }
          playWatchdogRef.current = window.setTimeout(verifyPlayback, 280)
          return
        }

        if (retryCount < 12) {
          retryCount += 1
          advancingSamples = 0
          lastObservedTime = currentTime
          // Autoplay recovery may need a muted start only before unlock.
          // After unlock, remuting permanently kills swipe audio.
          if (!audioUnlockedRef.current || mutedRef.current) {
            player.volume = 0
            player.muted = true
          } else {
            applyAudio(player)
          }
          try {
            // Nudge a frozen playhead; avoid loadVideoById (resets to a cold freeze).
            if (retryCount >= 2) {
              player.seekTo(currentTime > 0.2 ? currentTime : Math.max(0.05, currentTime + 0.05))
            }
            player.playVideo()
          } catch {
            /* watchdog will retry */
          }
          playWatchdogRef.current = window.setTimeout(verifyPlayback, 400)
          return
        }
      }

      playWatchdogRef.current = window.setTimeout(verifyPlayback, 160)
    },
    [applyAudio, clearPlayWatchdog],
  )

  const requestPlay = useCallback(
    (player: YoutubeShortsHandle, _userInitiated = false) => {
      if (!isActiveRef.current || !playingRef.current) return

      applyAudio(player)
      try {
        if (!hasActivePlaybackProgressRef.current && !activateKickRef.current) {
          activateKickRef.current = true
          const currentTime = Number(player.currentTime) || 0
          // Continue a warm peer in place. Only rewind if it drifted far.
          if (currentTime >= PROMOTE_REWIND_MIN_S) {
            try {
              player.seekTo(0)
            } catch {
              /* ignore */
            }
            promoteRewindPendingRef.current = true
            promoteRewindStartedAtRef.current = Date.now()
          }
          if (currentTime > 0.08 || warmBufferedRef.current) {
            hasActivePlaybackProgressRef.current = true
            hasStartedPlaybackRef.current = true
            hasPlaybackProgressRef.current = true
            setFrameReady(true)
          }
        }
        // Prefer continuing an already-playing warm peer (no new autoplay grant).
        if (!player.playing) {
          player.playVideo()
        }
        armPlayWatchdog(player)
      } catch {
        playingRef.current = false
        callbacksRef.current.onPlayingChange?.(false)
      }
    },
    [applyAudio, armPlayWatchdog],
  )

  const syncPlayback = useCallback(() => {
    const player = playerRef.current
    if (!player) return
    if (!isActiveRef.current) {
      clearPlayWatchdog()
      applyAudio(player)
      if (shouldWarmRef.current) {
        // Never cancel an in-flight warm delay/settle — that left +1 cold.
        if (!warmInFlightRef.current && !warmBufferedRef.current) {
          warmBuffer(player)
        } else {
          // Keep hard-mute and keep the stream alive so swipe hits a live frame.
          player.volume = 0
          player.muted = true
          const warmTime = Number(player.currentTime) || 0
          // Hold warm peers near the start, but never while they are on-screen.
          if (warmTime > 0.85 && !suppressWarmRewindRef.current) {
            try {
              player.seekTo(0.05)
            } catch {
              /* ignore */
            }
          }
          if (!player.playing) {
            try {
              player.playVideo()
            } catch {
              /* warm keep-alive */
            }
          }
        }
        return
      }
      // Preload-only peers keep the iframe mounted but must stay paused so we
      // never have 3+ YouTube streams competing for the media pipeline.
      clearWarmTimer()
      warmInFlightRef.current = false
      warmBufferedRef.current = false
      pausePlayer(player)
      return
    }
    // Promote to active: cancel neighbour warm settle so we don't pause ourselves.
    clearWarmTimer()
    warmInFlightRef.current = false
    if (!playingRef.current) {
      clearPlayWatchdog()
      applyAudio(player)
      pausePlayer(player)
      return
    }
    // Already advancing as the active short — only reassert audio.
    if (hasActivePlaybackProgressRef.current) {
      applyAudio(player)
      if (!player.playing) {
        try {
          player.playVideo()
        } catch {
          /* ignore */
        }
        armPlayWatchdog(player)
      }
      return
    }
    // Kick already issued; keep the warm stream alive without rewinding.
    if (activateKickRef.current) {
      applyAudio(player)
      if (!player.playing) {
        try {
          player.playVideo()
        } catch {
          /* watchdog recovers */
        }
      }
      armPlayWatchdog(player)
      return
    }
    requestPlay(player)
  }, [
    applyAudio,
    armPlayWatchdog,
    clearPlayWatchdog,
    clearWarmTimer,
    pausePlayer,
    requestPlay,
    warmBuffer,
  ])

  const togglePlayback = useCallback(() => {
    const player = playerRef.current
    if (!player || !isActiveRef.current) return

    if (
      playingRef.current &&
      (player.playing ||
        hasActivePlaybackProgressRef.current ||
        hasStartedPlaybackRef.current)
    ) {
      clearPlayWatchdog()
      playingRef.current = false
      pausePlayer(player)
      callbacksRef.current.onPlayingChange?.(false)
      return
    }

    playingRef.current = true
    callbacksRef.current.onPlayingChange?.(true)
    void requestPlay(player, true)
  }, [clearPlayWatchdog, pausePlayer, requestPlay])

  useEffect(() => {
    syncPlayback()
  }, [audioUnlocked, isActive, muted, playing, syncPlayback, volume])

  useEffect(() => {
    const handleVisibilityChange = () => {
      const player = playerRef.current
      if (!player) return
      if (document.hidden) {
        clearPlayWatchdog()
        pausePlayer(player)
        return
      }
      syncPlayback()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [clearPlayWatchdog, pausePlayer, syncPlayback])

  useEffect(() => {
    const root = mountRef.current
    const host = hostRef.current
    if (!root || !host || !shouldKeepPlayer) {
      return
    }

    let cancelled = false
    let iframe: HTMLIFrameElement | null = null
    hasStartedPlaybackRef.current = false
    hasPlaybackProgressRef.current = false
    hasActivePlaybackProgressRef.current = false
    seekingRef.current = false
    playerApiReadyRef.current = false
    setFrameReady(false)
    clearPlayWatchdog()
    // Keep UI black — never flash a loading spinner on Shorts hosts.
    queueMicrotask(() => {
      if (!cancelled) setStatus('ready')
    })

    const destroy = () => {
      clearPlayWatchdog()
      clearWarmTimer()
      warmBufferedRef.current = false
      warmInFlightRef.current = false
      if (progressTimerRef.current) {
        window.clearInterval(progressTimerRef.current)
        progressTimerRef.current = undefined
      }
      const player = playerRef.current
      if (player && playerApiReadyRef.current && iframe?.isConnected) {
        try {
          pausePlayer(player)
          player.destroy()
        } catch {
          /* provider may already be disposed */
        }
      }
      playerRef.current = null
      playerApiReadyRef.current = false
      iframe?.remove()
      iframe = null
    }

    destroy()

    // Muted autoplay via the embed URL is the only reliable way to start a
    // neighbour host; playVideo() after a long paused preload often freezes.
    const startsWithAutoplay =
      !document.hidden &&
      ((isActiveRef.current && playingRef.current) || shouldWarmRef.current)

    // Render the iframe immediately, as in a plain YouTube embed. The IFrame
    // API attaches to this existing element later only for feed orchestration;
    // it no longer delays or replaces the visible player.
    iframe = document.createElement('iframe')
    iframe.src = buildYoutubeEmbedUrl(startsWithAutoplay, youtubeIdRef.current)
    iframe.title = 'YouTube Shorts video oynatıcı'
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
    iframe.referrerPolicy = 'strict-origin-when-cross-origin'
    iframe.setAttribute('frameborder', '0')
    iframe.setAttribute('allowfullscreen', '')
    iframe.setAttribute('loading', 'eager')
    host.replaceChildren(iframe)

    const reportProgress = () => {
      if (cancelled) return
      const player = playerRef.current
      if (!player) return
      const currentTime = Number(player.currentTime) || 0
      // Neighbour warm progress must not mark this host as "already started".
      if (
        isActiveRef.current &&
        !hasPlaybackProgressRef.current &&
        currentTime > 0.05
      ) {
        hasPlaybackProgressRef.current = true
        hasStartedPlaybackRef.current = true
      }
      if (
        !cancelled &&
        currentTime > 0.05 &&
        (isActiveRef.current || shouldWarmRef.current || warmBufferedRef.current)
      ) {
        setFrameReady(true)
      }

      // Never push inactive/warm ticks to the shared feed bar — callbacksRef
      // can lag one effect cycle behind isActive and cause a mid-video jump.
      if (!isActiveRef.current) {
        if (
          !suppressWarmRewindRef.current &&
          (shouldWarmRef.current || warmBufferedRef.current) &&
          currentTime > 0.85
        ) {
          const now = Date.now()
          if (now - lastWarmRewindAtRef.current > 700) {
            lastWarmRewindAtRef.current = now
            try {
              player.seekTo(0.05)
            } catch {
              /* ignore */
            }
          }
        }
        return
      }

      if (promoteRewindPendingRef.current) {
        const waited = Date.now() - promoteRewindStartedAtRef.current
        if (currentTime > 0.4 && waited < 1200) {
          try {
            player.seekTo(0)
          } catch {
            /* ignore */
          }
          return
        }
        promoteRewindPendingRef.current = false
      }

      callbacksRef.current.onTimeUpdate?.(
        currentTime,
        Number(player.duration) || 0,
      )
      updateYoutubeProgress(player)
    }

    const initializePlayback = () => {
      if (cancelled || playbackInitialized) return
      playbackInitialized = true
      playerApiReadyRef.current = true
      setStatus('ready')
      if (!startsWithAutoplay) {
        syncPlayback()
        return
      }
      applyAudio(playerRef.current!)
      if (isActiveRef.current && playingRef.current) {
        armPlayWatchdog(playerRef.current!)
      } else if (shouldWarmRef.current) {
        warmBuffer(playerRef.current!)
      } else {
        pausePlayer(playerRef.current!)
      }
    }

    let playbackInitialized = false

    void loadYoutubeIframeApi()
      .then((YT) => {
        if (cancelled || !hostRef.current || !iframe) return

        const yt = new YT.Player(iframe, {
          events: {
            onReady: () => {
              if (cancelled) return
              const wrapped = wrapYoutubePlayer(yt)
              playerRef.current = wrapped
              initializePlayback()
            },
            onStateChange: (event) => {
              if (cancelled) return
              const player = playerRef.current
              if (!player) return

              if (event.data === YT.PlayerState.PLAYING) {
                if (!isActiveRef.current) {
                  // Warm neighbours must keep playing muted until buffered.
                  player.volume = 0
                  player.muted = true
                  if (shouldWarmRef.current || warmBufferedRef.current) {
                    setFrameReady(true)
                  }
                  if (
                    !warmInFlightRef.current &&
                    !shouldWarmRef.current &&
                    !shouldPreloadRef.current
                  ) {
                    pausePlayer(player)
                  }
                  return
                }
                if (!playingRef.current) {
                  pausePlayer(player)
                  return
                }
                hasStartedPlaybackRef.current = true
                setFrameReady(true)
                // loadVideoById can reset provider mute — reassert desired audio.
                applyAudio(player)
                callbacksRef.current.onPlayingChange?.(true)
                if (isActiveRef.current && playingRef.current) {
                  armPlayWatchdog(player)
                }
                return
              }

              if (
                event.data === YT.PlayerState.PAUSED &&
                hasStartedPlaybackRef.current &&
                isActiveRef.current &&
                !document.hidden &&
                playingRef.current
              ) {
                hasActivePlaybackProgressRef.current = false
                // Let the single playback watchdog recover an unexpected
                // provider pause. Sending a second play command here caused
                // stop/start oscillation while YouTube was buffering.
                armPlayWatchdog(player)
              }

              if (event.data === YT.PlayerState.ENDED) {
                callbacksRef.current.onPlayingChange?.(false)
              }
            },
            onError: () => {
              if (!cancelled) setStatus('error')
            },
          },
        })

        progressTimerRef.current = window.setInterval(() => {
          if (cancelled || !playerRef.current) return
          if (Number(playerRef.current.duration) > 0 && !playbackInitialized) {
            initializePlayback()
          }
          reportProgress()
        }, 250)

        window.setTimeout(() => {
          if (!cancelled && playerRef.current) {
            setStatus((current) => (current === 'loading' ? 'ready' : current))
          }
        }, 6000)
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })

    loadedVideoIdRef.current = youtubeIdRef.current

    return () => {
      cancelled = true
      destroy()
      loadedVideoIdRef.current = null
    }
    // Mount once per host lifetime. Video swaps use loadVideoById below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldKeepPlayer])

  useEffect(() => {
    const player = playerRef.current
    if (!player || !playerApiReadyRef.current) return
    if (loadedVideoIdRef.current === youtubeId) return

    loadedVideoIdRef.current = youtubeId
    hasStartedPlaybackRef.current = false
    hasPlaybackProgressRef.current = false
    hasActivePlaybackProgressRef.current = false
    activateKickRef.current = false
    warmBufferedRef.current = false
    warmInFlightRef.current = false
    clearPlayWatchdog()
    clearWarmTimer()
    setStatus('ready')
    setFrameReady(false)

    try {
      player.loadVideoById(youtubeId)
    } catch {
      setStatus('error')
      return
    }

    applyAudio(player)
    if (isActiveRef.current && playingRef.current) {
      try {
        player.playVideo()
      } catch {
        /* watchdog recovers */
      }
      armPlayWatchdog(player)
    } else if (shouldWarmRef.current) {
      warmBuffer(player)
    } else {
      pausePlayer(player)
    }
  }, [
    applyAudio,
    armPlayWatchdog,
    clearPlayWatchdog,
    clearWarmTimer,
    pausePlayer,
    warmBuffer,
    youtubeId,
  ])

  if (!shouldKeepPlayer) {
    return <div ref={mountRef} className={styles.shortYoutubeMount} />
  }

  return (
    <div
      className={`${styles.mediaLayer} ${styles.shortYoutubeHost}${!isActive && !shared ? ` ${styles.shortYoutubePreload}` : ''}`}
      data-shorts-player=""
      data-shorts-provider="youtube"
    >
      <div ref={mountRef} className={styles.shortYoutubeMount}>
        <div ref={hostRef} className={styles.shortYoutubeFrame} />
        {!frameReady ? (
          <div className={styles.shortYoutubeVeil} aria-hidden />
        ) : null}
      </div>
      {/* Shared feed hosts render the scrubber above the scroll layer. */}
      {isActive && !shared ? (
      <div className={styles.youtubeProgress} data-shorts-control>
        <input
          ref={youtubeProgressRef}
          type="range"
          min="0"
          max="100"
          step="0.05"
          defaultValue="0"
          aria-label="Video ilerleme"
          onPointerDown={(event) => {
            event.stopPropagation()
            seekingRef.current = true
          }}
          onPointerUp={(event) => {
            event.stopPropagation()
            seekingRef.current = false
            const player = playerRef.current
            if (player) updateYoutubeProgress(player)
          }}
          onPointerCancel={(event) => {
            event.stopPropagation()
            seekingRef.current = false
            const player = playerRef.current
            if (player) updateYoutubeProgress(player)
          }}
          onInput={(event) => {
            const player = playerRef.current
            if (!player) return
            const target = event.currentTarget
            const duration = Math.max(0, Number(player.duration) || 0)
            const nextTime = Math.min(
              duration || Number.MAX_SAFE_INTEGER,
              Math.max(0, Number(target.value) || 0),
            )
            player.seekTo(nextTime)
            const progress = duration > 0 ? (nextTime / duration) * 100 : 0
            target.style.setProperty('--shorts-progress', `${progress}%`)
          }}
        />
      </div>
      ) : null}
      {isActive ? (
        <div
          className={styles.playbackSurface}
          aria-label={playing ? 'Videoyu durdur' : 'Videoyu oynat'}
          tabIndex={0}
          onPointerDown={(event) => {
            playbackSurfacePointerRef.current = {
              pointerId: event.pointerId,
              startX: event.clientX,
              startY: event.clientY,
              moved: false,
            }
          }}
          onPointerMove={(event) => {
            const pointer = playbackSurfacePointerRef.current
            if (!pointer || pointer.pointerId !== event.pointerId) return
            if (
              Math.abs(event.clientX - pointer.startX) > 10 ||
              Math.abs(event.clientY - pointer.startY) > 10
            ) {
              pointer.moved = true
            }
          }}
          onPointerUp={(event) => {
            const pointer = playbackSurfacePointerRef.current
            if (!pointer || pointer.pointerId !== event.pointerId) return
            if (pointer.moved) {
              suppressPlaybackSurfaceClickUntilRef.current = Date.now() + 400
            }
            playbackSurfacePointerRef.current = null
          }}
          onPointerCancel={(event) => {
            const pointer = playbackSurfacePointerRef.current
            if (!pointer || pointer.pointerId !== event.pointerId) return
            if (pointer.moved) {
              suppressPlaybackSurfaceClickUntilRef.current = Date.now() + 400
            }
            playbackSurfacePointerRef.current = null
          }}
          onClick={(event) => {
            if (Date.now() < suppressPlaybackSurfaceClickUntilRef.current) {
              event.preventDefault()
              return
            }
            togglePlayback()
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            togglePlayback()
          }}
        />
      ) : null}
      {status === 'error' ? (
        <div className={styles.shortPlyrError}>Video yüklenemedi</div>
      ) : null}
    </div>
  )
}
